import { CUSTOM_ELEMENTS_SCHEMA, NgModule, NO_ERRORS_SCHEMA, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from './material.module';

import { QuillModule } from 'ngx-quill';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';

import { metaReducers, reducers } from './reducers';

// Modules
import { SharedModule } from './shared/shared.module';
import { CoreModule } from './core/core.module';
import { AppRoutingModule } from './app-routing.module';
import { CfaLoadingModule } from './cfa-control-modules/cfa-loading/cfa-loading-module';

// Effects
import { CustomerEffects } from './effects/customer';
import { MeetEffects } from './effects/meet';
import { CareEffects } from './effects/care';
import { ProfileEffects } from './effects/profile';
import { DiscoverEffects } from './effects/discover';
import { EngagementEffects } from './effects/engagement';
import { SettingsEffects } from './effects/settings';
import { UserEffects } from './effects/user';
import { EulaEffects } from './effects/eula';
import { UnderstandEffects } from './effects/understand';
import { EngageEffects } from './effects/engage';
import { CalendarEffects } from './effects/calendar';
import { NotificationEffects } from './effects/notifications';
import { ActiveRewardsEffects } from './effects/active-rewards';
import { AutomationsEffects } from './effects/automations';

// Services
import { UserService } from './core/user';

// Components
import { AppComponent } from './app.component';
import { CallbackComponent } from './callback.component';

//okta
import { OktaAuthModule, OktaConfig } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import oktaConfig from './app.config';
import { CookieStorageProvider } from './shared/okta/cookie-storage-provider';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { AuthInterceptor } from './shared/okta/auth.interceptor';
import { provideDatadogRouter } from '@datadog/browser-rum-angular';
import { NavigationService } from './navigation.service';
import { RoleGuard } from './roleGuard.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { LaunchDarklyService } from './launch-darkly.service';
import { launchDarklyFactory } from './launch-darkly-factory';
import { CampaignEngageEffects } from './effects/campaign-engage';
import { CUSTOMER_SOURCE_INJECTION_TOKEN, LegacySpotlightCustomerSource } from './core/customer.service';
import { RefreshService } from './core/refresh.service';
import { OktaSetupService } from './okta-setup.service';

// Storage key Okta uses by default; both V2 and legacy must agree on this for
// the shared-cookie bridge to work.
const TOKEN_STORAGE_KEY = 'okta-token-storage';

const oktaAuth = new OktaAuth({
  ...oktaConfig.oidc,
  storageManager: {
    token: {
      storageKey: TOKEN_STORAGE_KEY,
      storageProvider: new CookieStorageProvider({
        secure: true,
        sameSite: 'Lax',
        sessionCookie: true,
      }),
    },
  },
  tokenManager: {
    ...oktaConfig.oidc.tokenManager,
    storageKey: TOKEN_STORAGE_KEY,
  },
});
const moduleConfig: OktaConfig = { oktaAuth };

/**
 * APP_INITIALIZER factory that waits for Okta authentication
 * before allowing the application to start
 */
export function oktaAuthInitializer(oktaSetupService: OktaSetupService) {
  return () => oktaSetupService.initializeApp();
}

@NgModule({
  bootstrap: [AppComponent],
  imports: [
    StoreModule.forRoot(reducers, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false
      },
      metaReducers
    }),
    StoreDevtoolsModule.instrument({ maxAge: 50 }),
    EffectsModule.forRoot([
      CustomerEffects,
      MeetEffects,
      CareEffects,
      ProfileEffects,
      DiscoverEffects,
      EngagementEffects,
      UnderstandEffects,
      EngageEffects,
      CampaignEngageEffects,
      CalendarEffects,
      NotificationEffects,
      ActiveRewardsEffects,
      SettingsEffects,
      UserEffects,
      EulaEffects,
      AutomationsEffects
    ]),
    SharedModule.forRoot(),
    QuillModule.forRoot(),
    BrowserModule,
    AppRoutingModule,
    MaterialModule,
    BrowserAnimationsModule,
    CoreModule,
    CfaLoadingModule,
    OktaAuthModule.forRoot(moduleConfig)
  ],
  schemas: [ CUSTOM_ELEMENTS_SCHEMA , NO_ERRORS_SCHEMA],
  declarations: [
    AppComponent,
    CallbackComponent
  ],
  providers: [
    UserService,
    LaunchDarklyService,
    NavigationService,
    OktaSetupService,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    RefreshService,
    RoleGuard,
    provideDatadogRouter(),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
        appearance: 'outline'
      }
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideAppInitializer(() => {
      const initializerFn = (oktaAuthInitializer)(inject(OktaSetupService));
      return initializerFn();
    }),
    { provide: launchDarklyFactory, useValue: launchDarklyFactory },
    { provide: CUSTOMER_SOURCE_INJECTION_TOKEN, useClass: LegacySpotlightCustomerSource }
  ]
})
export class AppModule {}
